import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { twpApiURL } from 'src/api.js';
import { finalizeTransformer } from 'helpers/finalizeTransformer';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { deepEqual } from 'swipeclock-frontend';

export const getFinalizePayPeriods = createAsyncThunk(
  'finalizePayPeriod/getFinalizePayPeriods',
  async (_, { getState, rejectWithValue }) => {
    const user = await getState().user;
    const { token } = user;
    const api = twpApiURL(user);

    try {
      const response = await api.getUnfinalizedPayPeriods();
      return finalizeTransformer(response, token);
    } catch (error) {
      return rejectWithValue({
        error,
      });
    }
  }
);

export const getUnfinalizePayPeriods = createAsyncThunk(
  'finalizePayPeriod/getUnfinalizePayPeriods',
  async (_, { getState, rejectWithValue }) => {
    const user = await getState().user;
    const { token } = user;
    const api = twpApiURL(user);

    try {
      const response = await api.getFinalizedPayPeriods();
      const payPeriods = finalizeTransformer(response, token);
      // The finalizeTransformer takes dates and reverses the array that
      // comes back from the api. This assumes the api is sending things in ASC
      // order meaning earliest date comes first: i.e., 1900, 1901, 1902, etc.
      // By reversing it makes this DESC; however to be safe and sure, we
      // map out the endDates and then use Date.parse to convert string dates to
      // a number of milliseconds ( instead of init new dates evey time. Small perf boost).
      // If we find a sorted end date then we set that to 'lastFinalizedDate'.
      const lastFinalizedDate = payPeriods
        .map((payPeriod) => payPeriod.endDate)
        .sort((a, b) => Date.parse(b) - Date.parse(a))?.[0];
      return {
        unfinalizePayPeriods: payPeriods,
        lastFinalizedDate,
      };
    } catch (error) {
      return rejectWithValue({
        error,
      });
    }
  }
);

export const setFinalizePayPeriods = createAsyncThunk(
  'finalizePayPeriod/setFinalizePayPeriods',
  async (args, { getState, dispatch, rejectWithValue }) => {
    const { finalize } = args;
    const user = await getState().user;
    const selectedPayPeriod = await getState().finalizePayPeriod
      .selectedPayPeriod;
    const api = twpApiURL(user);
    const endpoint = finalize ? 'finalize' : 'unfinalize';

    try {
      await api.finalizePayPeriods(selectedPayPeriod, endpoint);

      return finalize
        ? dispatch(getFinalizePayPeriods())
        : dispatch(getUnfinalizePayPeriods());
    } catch (error) {
      return rejectWithValue({
        error,
      });
    }
  }
);

const finalizePayPeriodSlice = createSlice({
  name: 'finalized Pay Period',
  initialState: {
    lastFinalizedDate: undefined,
    finalizePayPeriods: [],
    unfinalizePayPeriods: [],
    selectedPayPeriod: {},
    isLoading: false,
  },
  reducers: {
    setSelectedPayPeriod(state, action) {
      state.selectedPayPeriod = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setFinalizePayPeriods.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setFinalizePayPeriods.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(setFinalizePayPeriods.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getFinalizePayPeriods.fulfilled, (state, action) => {
        state.finalizePayPeriods = action.payload;
      })
      .addCase(getUnfinalizePayPeriods.fulfilled, (state, action) => {
        state.unfinalizePayPeriods = action.payload.unfinalizePayPeriods;
        state.lastFinalizedDate = action.payload.lastFinalizedDate;
      });
  },
});

export const { setSelectedPayPeriod } = finalizePayPeriodSlice.actions;

export const lastFinalizedDateSelector = R.path([
  'finalizePayPeriod',
  'lastFinalizedDate',
]);

export const finalizedPayPeriodsSelector = R.path([
  'finalizePayPeriod',
  'finalizePayPeriods',
]);

export function useLastFinalizedDate() {
  return useSelector(lastFinalizedDateSelector, deepEqual);
}

export function useFirstUnfinalizedDate() {
  const finalizedPayPeriods = useSelector(
    finalizedPayPeriodsSelector,
    deepEqual
  );
  const firstUnfinalizedDate = finalizedPayPeriods
    .map((payPeriod) => payPeriod.beginDate)
    .sort((a, b) => Date.parse(a) - Date.parse(b))?.[0];
  return firstUnfinalizedDate;
}

export default finalizePayPeriodSlice.reducer;
